import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Phone, Heart, MapPin, FileText, Plus, X, Save } from 'lucide-react';

const EmergencyPlanEditor = ({ formData, setFormData, onSave, onCancel, hasExistingPlan }) => {
  const handleAddContact = () => {
    setFormData({
      ...formData,
      emergency_contacts: [...formData.emergency_contacts, { name: '', relationship: '', phone: '', email: '' }]
    });
  };

  const handleRemoveContact = (index) => {
    setFormData({ ...formData, emergency_contacts: formData.emergency_contacts.filter((_, i) => i !== index) });
  };

  const handleContactChange = (index, field, value) => {
    const newContacts = [...formData.emergency_contacts];
    newContacts[index][field] = value;
    setFormData({ ...formData, emergency_contacts: newContacts });
  };

  return (
    <div className="space-y-6">
      <Card className="surface-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-destructive" /> Emergency Contacts
          </CardTitle>
          <CardDescription>People to contact in case of emergency</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {formData.emergency_contacts.map((contact, index) => (
            <div key={`contact-${index}-${contact.name || 'new'}`} className="bg-muted/30 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-foreground">Contact {index + 1}</h4>
                <Button variant="ghost" size="icon" onClick={() => handleRemoveContact(index)} data-testid={`remove-contact-${index}`}>
                  <X className="w-4 h-4 text-destructive" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Name" value={contact.name} onChange={(e) => handleContactChange(index, 'name', e.target.value)} data-testid={`contact-name-${index}`} />
                <Input placeholder="Relationship" value={contact.relationship} onChange={(e) => handleContactChange(index, 'relationship', e.target.value)} data-testid={`contact-relationship-${index}`} />
                <Input placeholder="Phone" type="tel" value={contact.phone} onChange={(e) => handleContactChange(index, 'phone', e.target.value)} data-testid={`contact-phone-${index}`} />
                <Input placeholder="Email (optional)" type="email" value={contact.email} onChange={(e) => handleContactChange(index, 'email', e.target.value)} data-testid={`contact-email-${index}`} />
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={handleAddContact} className="w-full" data-testid="add-contact-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Emergency Contact
          </Button>
        </CardContent>
      </Card>

      <Card className="surface-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Heart className="w-5 h-5 text-destructive" /> Medical Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="medical-conditions">Medical Conditions</Label>
            <Textarea id="medical-conditions" placeholder="List any chronic conditions, disabilities, or ongoing health issues" value={formData.medical_conditions} onChange={(e) => setFormData({ ...formData, medical_conditions: e.target.value })} data-testid="medical-conditions-input" rows={3} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="allergies">Allergies</Label>
            <Textarea id="allergies" placeholder="Medication allergies, food allergies, etc." value={formData.allergies} onChange={(e) => setFormData({ ...formData, allergies: e.target.value })} data-testid="allergies-input" rows={2} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="medications">Current Medications</Label>
            <Textarea id="medications" placeholder="List all current medications with dosages" value={formData.current_medications} onChange={(e) => setFormData({ ...formData, current_medications: e.target.value })} data-testid="medications-input" rows={3} />
          </div>
        </CardContent>
      </Card>

      <Card className="surface-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5 text-destructive" /> Healthcare Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="insurance">Insurance Information</Label>
            <Textarea id="insurance" placeholder="Insurance provider, policy number, contact info" value={formData.insurance_info} onChange={(e) => setFormData({ ...formData, insurance_info: e.target.value })} data-testid="insurance-input" rows={2} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="hospital">Preferred Hospital</Label>
            <Input id="hospital" placeholder="Hospital name and address" value={formData.preferred_hospital} onChange={(e) => setFormData({ ...formData, preferred_hospital: e.target.value })} data-testid="hospital-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="doctor">Primary Care Doctor</Label>
            <Input id="doctor" placeholder="Doctor name and contact" value={formData.primary_care_doctor} onChange={(e) => setFormData({ ...formData, primary_care_doctor: e.target.value })} data-testid="doctor-input" />
          </div>
        </CardContent>
      </Card>

      <Card className="surface-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-destructive" /> Emergency Action Plan</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="action-steps">Step-by-Step Emergency Response</Label>
            <Textarea id="action-steps" placeholder="What to do in an emergency..." value={formData.action_steps} onChange={(e) => setFormData({ ...formData, action_steps: e.target.value })} data-testid="action-steps-input" rows={4} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="documents">Important Documents Location</Label>
            <Textarea id="documents" placeholder="Where to find insurance cards, advance directives, etc." value={formData.important_documents} onChange={(e) => setFormData({ ...formData, important_documents: e.target.value })} data-testid="documents-input" rows={3} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="special-instructions">Special Instructions</Label>
            <Textarea id="special-instructions" placeholder="Any other critical information..." value={formData.special_instructions} onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })} data-testid="special-instructions-input" rows={3} />
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button onClick={onSave} className="flex-1 btn-primary" data-testid="save-plan-btn">
          <Save className="w-4 h-4 mr-2" /> Save Emergency Plan
        </Button>
        {hasExistingPlan && (
          <Button onClick={onCancel} variant="outline" className="flex-1">Cancel</Button>
        )}
      </div>
    </div>
  );
};

export default EmergencyPlanEditor;
